import { createContext, useContext } from 'react';

import useResendStudy from 'components/Flux/Personalized/MyStudies/ActionsCell/useResendStudy';
import usePersonalizedRowActions from 'components/Flux/Personalized/Row/useStudyRowActions';
import { formatComposition } from 'helper/flux/myStudyRow';
import { myStudiesApiPort, MyStudiesApiPort } from 'services/api/myStudies';
import usePersonalizedDownload from 'utils/hooks/flux/personalized/usePersonalizedDownload';
import { MyStudy } from 'types/Flux/Personalized/myStudies';

/** O que varia entre o Flux Personalizado e o Flux Upload na aba Meus Estudos. */
export interface MyStudiesProduct {
  key: 'personalized' | 'upload';
  api: MyStudiesApiPort;
  /** Prefixos de storage, um por produto (a chave final ganha `:<userId>`). */
  storage: { filtersPrefix: string; noticePrefix: string };
  /** Painel de cenários na linha (só o Personalizado). */
  expandable: boolean;
  /** Texto sob o horizonte: "N cenários · M blocos" ou o nome do zip; `null` = sem detalhe. */
  rowDetail: (study: MyStudy) => string | null;
  /** Estado 4 da lista: empresa sem nenhum estudo. */
  emptyCompany: { title: string; text: string; cta: string; newStudyPath: string };
  useDownload: typeof usePersonalizedDownload;
  useRowActions: typeof usePersonalizedRowActions;
  useResend: typeof useResendStudy;
}

export const PERSONALIZED_PRODUCT: MyStudiesProduct = {
  key: 'personalized',
  api: myStudiesApiPort,
  storage: {
    filtersPrefix: 'flux-meus-estudos-filtros-v1',
    noticePrefix: 'flux-meus-estudos-aviso-casos-ampere-v1',
  },
  expandable: true,
  rowDetail: formatComposition,
  emptyCompany: {
    title: 'Sua empresa ainda não tem estudos personalizados',
    text: 'Monte cenários com modelo, data da previsão e membro. O estudo entra na fila e você baixa os resultados em ZIP quando terminar.',
    cta: 'Novo estudo personalizado',
    newStudyPath: '/flux/personalized/new',
  },
  useDownload: usePersonalizedDownload,
  useRowActions: usePersonalizedRowActions,
  useResend: useResendStudy,
};

/**
 * Produto da aba Meus Estudos. O default é o Personalizado: um componente ou hook renderizado sem
 * Provider (toda a árvore atual, todos os testes existentes) se comporta exatamente como antes.
 * A casca `MyStudies` monta o Provider com o produto recebido por prop; um componente do Upload
 * renderizado FORA dela cai em silêncio no Personalizado (a view e o `product.test` cobrem).
 */
export const MyStudiesProductContext = createContext<MyStudiesProduct>(PERSONALIZED_PRODUCT);

export function useMyStudiesProduct(): MyStudiesProduct {
  return useContext(MyStudiesProductContext);
}
